import { Container, Tabs, Typography, Tab, Box, Skeleton } from "@mui/material";
import React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import AccountModal from "../components/AccountModal";
import { URL, origin } from "../config";
import { Doughnut, Line } from "react-chartjs-2";
import Loading from "../components/Loading";
import { Helmet } from "react-helmet-async";
export default function CommbankHomePage() {
    const theme = useTheme();

    const formatter = Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "AUD",
    });

    const lineOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: theme.palette.primary.contrastText,
                },
            },
            title: {
                display: true,
                text: "Commbank accounts value over time",
                color: theme.palette.primary.contrastText,
            },
        },
        scales: {
            y: {
                grid: {
                    color: theme.palette.primary.contrastText,
                },
                ticks: {
                    color: theme.palette.primary.contrastText,
                },
            },
            x: {
                ticks: {
                    color: theme.palette.primary.contrastText,
                },
            },
        },
    };

    const dougnutOptions = {
        plugins: {
            title: {
                display: true,
                text: "Commbank accounts breakdown",
                color: theme.palette.primary.contrastText,
            },
            legend: {
                display: true,
                labels: {
                    color: theme.palette.primary.contrastText,
                },
            },
        },
    };

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setlastUpdated] = useState();
    const navigate = useNavigate();
    const [accounts, setAccounts] = useState([]);
    const [total, setTotal] = useState(0);
    const [value, setValue] = useState(0);
    const [doughnutData, setDoughnutData] = useState({});
    const [lineData, setLineData] = useState({});

    function fetchData() {
        setLoading(true);
        fetch(`${URL}/fetch/commbank`, {
            method: "GET",
            mode: "cors",
            credentials: "include",
            headers: {
                "content-type": "application/json",
                Origin: `${origin}`,
            },
        })
            .then((response) => {
                if (response.ok !== true) {
                    setError(response.statusText);
                    navigate(`/${response.status}`);
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then((data) => {
                setTotal(data.data.totalValue);
                setAccounts(
                    data.data.accounts.map((x) => {
                        const res = {};
                        res["accountName"] = x.accountName;
                        res["balance"] = x.balance;
                        res["currency"] = x.currency;

                        return res;
                    })
                );
                setlastUpdated(
                    new Date(data.lastUpdatedOn).toLocaleString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true, // 12-hour time format
                    })
                );
                setDoughnutData(createDoghnutData(data));
                setLineData(createLineData(data));
                setLoading(false);
            });
    }

    useEffect(() => {
        fetchData();
    }, []);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    function createColors() {
        let g = Math.floor(Math.random() * (255 - 180)) + 180; // random integer between 180 and 255
        return "rgb(255," + g + ",0)";
    }

    function createDoghnutData(data) {
        const colors = [];
        const accounts = data.data.accounts.map((x) => {
            const res = {};

            res["accountName"] = x.accountName;
            res["balance"] = x.balance;
            res["currency"] = x.currency;

            return res;
        });
        const accountBalances = accounts.map((x) => x.balance);
        const accountNames = accounts.map((x) => x.accountName);
        for (let i in accountNames) {
            colors.push(createColors());
        }
        const graph = {
            labels: accountNames,
            datasets: [
                {
                    label: "value AUD",
                    data: accountBalances,
                    backgroundColor: colors,
                    BorderColor: colors,
                    borderWidth: 1,
                },
            ],
        };

        return graph;
    }

    function createLineData(data) {
        const totalHistory = data.totalHistory.filter((x) => x.commbank);
        const totalLineData = totalHistory.map((x) => {
            const a = {};
            a["timestamp"] = x["timestamp"];
            a["value"] = x.commbank.total;
            return a;
        });

        const result = totalLineData.reduce((a, b) => {
            a[b.timestamp.slice(0, 10)] = b.value;
            return a;
        }, {});

        const graph = {
            labels: Object.keys(result),
            datasets: [
                {
                    label: "Accounts total over time",
                    data: Object.values(result),
                    backgroundColor: "rgba(240, 205, 10, 0.8)",
                    BorderColor: "rgba(240, 205, 10)",
                    color: "white",
                    fill: true,
                },
            ],
        };
        return graph;
    }

    return (
        <>
            <Helmet>
                <title>WealthWatch | Commbank</title>
            </Helmet>
            <Container
                maxWidth="xl"
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(12, 1fr)",
                    gridAutoRows: "100px",
                    my: 2,
                    gap: "20px",
                    "@media (max-width: 75em)": {
                        flexDirection: "column",
                        display: "flex",
                    },
                }}
            >
                <Box
                    gridColumn="span 6"
                    gridRow="span 6"
                    sx={{
                        m: 1,
                        backgroundColor: "primary.main",
                        borderRadius: 1,
                        boxShadow: 14,
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                    }}
                >
                    <Container sx={{ height: "90%", width: "90%" }}>
                        {loading ? (
                            <Loading />
                        ) : (
                            <Doughnut
                                data={doughnutData}
                                options={dougnutOptions}
                            />
                        )}
                    </Container>
                    <Container>
                        <Typography
                            color="green"
                            textAlign="center"
                            variant="h5"
                            my={2}
                        >{`Total value: ${formatter.format(
                            total
                        )}`}</Typography>
                    </Container>
                </Box>
                <Box
                    gridColumn="span 6"
                    gridRow="span 6"
                    sx={{
                        m: 1,
                        backgroundColor: "primary.main",
                        borderRadius: 1,
                        boxShadow: 14,
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                        "@media (max-width: 75em)": {
                            minHeight: "40vh",
                        },
                    }}
                >
                    {loading ? (
                        <Skeleton />
                    ) : (
                        <Line
                            data={lineData}
                            options={lineOptions}
                            style={{ gridColumn: "auto", gridRow: "auto" }}
                        />
                    )}
                </Box>

                <Box gridRow="span 7" gridColumn="span 12" sx={{ my: 2 }}>
                    <Typography
                        textAlign="center"
                        color="primary.contrastText"
                        variant="h5"
                    >
                        Accounts details
                    </Typography>
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            m: 1,
                        }}
                        indicatorColor="secondary"
                        textColor="secondary"
                    >
                        {accounts.map((account, index) => (
                            <Tab label={account.accountName} key={index} />
                        ))}
                    </Tabs>
                    {accounts.map((account, index) => (
                        <AccountModal
                            account={account.accountName}
                            index={index}
                            value={value}
                            key={index}
                        />
                    ))}
                </Box>
            </Container>
        </>
    );
}
